/**
 * Whether a product metric went up or down against the version before, and whether that is good.
 *
 * The arrow and the verdict are kept apart on purpose. The false-alarm rate is the reason: it
 * going down is an arrow down *and* an improvement, so a screen that coloured the arrow by its
 * direction would call the best news of a release bad news. The direction of «better» is the
 * metric's own, from `SERIES_METRICS`, the same place the targets live.
 *
 * Nothing here is computed from the videos: both sides are percentages Virtual Profiler froze in
 * a measurement, and this only subtracts them.
 */
import type { MetricsSnapshot } from '../model.js';
import {
  measuresTheData,
  type MetricDirection,
  SERIES_METRICS,
  triggerLabel,
} from './metrics-series.js';

export type TrendArrow = 'up' | 'down' | 'flat';

export type TrendVerdict = 'better' | 'worse' | 'same';

export interface MetricTrend {
  previous: number;
  /** Current minus previous, in percentage points, to one decimal. */
  deltaPts: number;
  arrow: TrendArrow;
  verdict: TrendVerdict;
}

const directionByKey = new Map(SERIES_METRICS.map((metric) => [metric.key, metric.direction]));

export function metricTrend(
  metricKey: string,
  current: number | null,
  previous: number | null,
): MetricTrend | null {
  const direction = directionByKey.get(metricKey);

  return direction ? directedTrend(direction, current, previous) : null;
}

/**
 * The same reading for a figure that is not one of the product's three — the gestures control's
 * detection, say — whose direction of «better» travels with it instead of living in
 * `SERIES_METRICS`.
 */
export function directedTrend(
  direction: MetricDirection,
  current: number | null,
  previous: number | null,
): MetricTrend | null {
  if (current === null || previous === null) return null;
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;

  // One decimal, so 70.3 − 70.1 is 0.2 and not 0.19999999999999574.
  const deltaPts = Math.round((current - previous) * 10) / 10;

  if (deltaPts === 0) return { previous, deltaPts: 0, arrow: 'flat', verdict: 'same' };

  const arrow: TrendArrow = deltaPts > 0 ? 'up' : 'down';
  const improved = direction === 'higher' ? deltaPts > 0 : deltaPts < 0;

  return { previous, deltaPts, arrow, verdict: improved ? 'better' : 'worse' };
}

/** The change in points, signed, with a real minus sign. */
export function formatTrendDelta(deltaPts: number): string {
  if (deltaPts === 0) return '0 pts';

  return `${deltaPts > 0 ? '+' : '−'}${Math.abs(deltaPts)} pts`;
}

/** Newest first: the version a screen opens on is the last one measured. */
export function versionsNewestFirst(snapshots: readonly MetricsSnapshot[]): MetricsSnapshot[] {
  return [...snapshots].sort((a, b) => b.computedAt.getTime() - a.computedAt.getTime());
}

/** The version measured just before the given one, or null when it is the first. */
export function previousVersion(
  newestFirst: readonly MetricsSnapshot[],
  snapshotId: string,
): MetricsSnapshot | null {
  const index = newestFirst.findIndex((snapshot) => snapshot.snapshotId === snapshotId);

  return index === -1 ? null : (newestFirst[index + 1] ?? null);
}

const MOMENT = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * What a version is called: the configuration it ran with, and when it was measured.
 *
 * The moment is always there because one configuration can be measured more than once, and two
 * entries reading only «Config v6» could not be told apart.
 *
 * A point the answer key moved is named by that instead: nothing ran, and a configuration beside
 * it would read as a run.
 */
export function versionLabel(snapshot: MetricsSnapshot): string {
  const moment = MOMENT.format(snapshot.computedAt);

  if (measuresTheData(snapshot.trigger)) return `${triggerLabel(snapshot.trigger)} · ${moment}`;

  return snapshot.configVersion === null ? moment : `Config v${snapshot.configVersion} · ${moment}`;
}
